import BottomErrorBar from 'components/bottom-error-bar';
import UPButton from 'components/button';
import GeneralConfirmModal from 'components/general-confirm-modal';
import UPLoadingSpinner from 'components/loading-spinner';
import WarningBar from 'components/warning-bar';
import { MfaPlatform } from 'constants/MfaPlatform';
import { MfaRole } from 'constants/MfaRole';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store';
import {
  clearAgencyMfaAction,
  discardAgencyMfaDraftAction,
  getAgencyMfaPolicyAction,
  saveAgencyMfaPolicyAction,
  setAgencyMfaDraftAction,
  setAgencyMfaErrorAction,
} from 'store/actions/agency-mfa-actions';
import {
  agencyMfaDraftSelector,
  agencyMfaErrorSelector,
  agencyMfaLoadingSelector,
  agencyMfaSavedSelector,
  agencyMfaSavingSelector,
} from 'store/selectors/agency-mfa-selectors';
import {
  SECURITY__CONFIRM_ENFORCEMENT_IMMEDIATE,
  SECURITY__CONFIRM_ENFORCEMENT_INCREASED,
  SECURITY__DISCARD_CHANGES,
  SECURITY__FAILS_CLOSED_NOTICE,
  SECURITY__MANDATORY_ACTIVATION,
  SECURITY__REQUIRED_BY_ROLE_AND_PLATFORM,
  SECURITY__SAVE_POLICY,
} from 'translations/constants';
import {
  getRequiredConfirmation,
  hasGracePeriodElapsed,
  isDateDirty,
  isMatrixDirty,
  isPolicyDirty,
  MfaConfirmation,
  setCell,
} from './helpers';
import MfaActivationDate from './MfaActivationDate';
import MfaMatrix from './MfaMatrix';
import { FooterActions, MfaSectionTitle, NoticeRow, SecurityBody, SecurityContainer } from './styles';

/**
 * Agency MFA enforcement matrix, in the Security & authentication configuration domain.
 *
 * No role guard here: this route sits inside the Management section, which RequireRole already
 * restricts to Agency SuperAdmins. The API independently returns a bare 403 to anyone else.
 */
const SecurityAuthentication = (): JSX.Element => {
  const [t] = useTranslation();
  const dispatch = useAppDispatch();

  const saved = useAppSelector(agencyMfaSavedSelector);
  const draft = useAppSelector(agencyMfaDraftSelector);
  const loading = useAppSelector(agencyMfaLoadingSelector);
  const saving = useAppSelector(agencyMfaSavingSelector);
  const errorKey = useAppSelector(agencyMfaErrorSelector);

  const [pendingConfirmation, setPendingConfirmation] = useState<MfaConfirmation>(MfaConfirmation.NONE);

  useEffect(() => {
    dispatch(getAgencyMfaPolicyAction());
    return () => {
      dispatch(clearAgencyMfaAction());
    };
  }, [dispatch]);

  const dirty = useMemo(() => (draft && saved ? isPolicyDirty(draft, saved) : false), [draft, saved]);

  /**
   * A stored date that has aged past today is fine and stays saveable. A past date the SuperAdmin
   * newly picks is not — US 590 rejects it server-side, so block the save instead of round-tripping
   * the error.
   */
  const activationDateRejected = useMemo(
    () => (draft && saved ? isDateDirty(draft, saved) && hasGracePeriodElapsed(draft.mandatoryActivationDate) : false),
    [draft, saved],
  );

  const onCellChange = useCallback(
    (role: MfaRole, platform: MfaPlatform, isRequired: boolean) => {
      if (saving) {
        return;
      }
      dispatch(setAgencyMfaDraftAction({ ...draft, cells: setCell(draft.cells, role, platform, isRequired) }));
    },
    [dispatch, draft, saving],
  );

  const onDateChange = useCallback(
    (value: string | null) => {
      dispatch(setAgencyMfaDraftAction({ ...draft, mandatoryActivationDate: value }));
    },
    [dispatch, draft],
  );

  const persist = useCallback(() => {
    dispatch(
      saveAgencyMfaPolicyAction({
        policy: draft,
        matrixDirty: isMatrixDirty(draft, saved),
        dateDirty: isDateDirty(draft, saved),
      }),
    );
  }, [dispatch, draft, saved]);

  const onSave = useCallback(() => {
    const confirmation = getRequiredConfirmation(draft, saved);
    if (confirmation === MfaConfirmation.NONE) {
      persist();
      return;
    }
    setPendingConfirmation(confirmation);
  }, [draft, saved, persist]);

  if (loading || !draft) {
    return <UPLoadingSpinner />;
  }

  return (
    <SecurityContainer>
      <SecurityBody>
        <MfaSectionTitle>{t(SECURITY__REQUIRED_BY_ROLE_AND_PLATFORM)}</MfaSectionTitle>
        <MfaMatrix cells={draft.cells} onChange={onCellChange} />

        <MfaSectionTitle>{t(SECURITY__MANDATORY_ACTIVATION)}</MfaSectionTitle>
        <MfaActivationDate
          value={draft.mandatoryActivationDate}
          savedValue={saved?.mandatoryActivationDate ?? null}
          onChange={onDateChange}
          disabled={saving}
        />

        <NoticeRow>
          <WarningBar message={t(SECURITY__FAILS_CLOSED_NOTICE)} variant="info" />
        </NoticeRow>

        <FooterActions>
          <UPButton
            outlined
            text={t(SECURITY__DISCARD_CHANGES)}
            disabled={!dirty || saving}
            onClick={() => dispatch(discardAgencyMfaDraftAction())}
            testId="mfa-discard-changes"
          />
          <UPButton
            text={t(SECURITY__SAVE_POLICY)}
            disabled={!dirty || activationDateRejected || saving}
            onClick={onSave}
            testId="mfa-save-policy"
          />
        </FooterActions>

        {pendingConfirmation !== MfaConfirmation.NONE && (
          <GeneralConfirmModal
            open
            content={t(
              pendingConfirmation === MfaConfirmation.ENFORCEMENT_IMMEDIATE
                ? SECURITY__CONFIRM_ENFORCEMENT_IMMEDIATE
                : SECURITY__CONFIRM_ENFORCEMENT_INCREASED,
            )}
            onClose={() => setPendingConfirmation(MfaConfirmation.NONE)}
            onAction={() => {
              setPendingConfirmation(MfaConfirmation.NONE);
              persist();
            }}
          />
        )}

        {errorKey && <BottomErrorBar message={t(errorKey)} onClose={() => dispatch(setAgencyMfaErrorAction(null))} />}
      </SecurityBody>
    </SecurityContainer>
  );
};

export default SecurityAuthentication;
